<template>
  <div class="p-0">
    <h1 class="text-2xl font-bold text-gray-800 mb-6">Compensatorios</h1>

    <!-- Select Usuario -->
    <div class="flex flex-col mb-6">
      <label class="text-gray-700 mb-2" for="janitor">Personal</label>
      <select
        class="w-full mt-2 border-gray-200 rounded-md focus:border-indigo-600 focus:ring focus:ring-opacity-40 focus:ring-indigo-500"
        id="janitor"
        name="janitor"
        v-model="selectedJanitor"
      >
        <option value="">Seleccione un personal</option>
        <option
          v-for="janitor in janitors"
          :key="janitor.id"
          :value="janitor.User ? janitor.User.id : janitor.id"
        >
          {{ janitor.Profile ? `${janitor.Profile.lastname}, ${janitor.Profile.firstname}` : janitor.name || janitor.email }}
        </option>
      </select>
    </div>

    <!-- Horas Disponibles -->
    <div
      v-if="selectedJanitor && !loadingCompensatories && !loadingSpentHours"
      :class="[
        'mb-6 p-4 rounded-lg border',
        availableHours < 0
          ? 'bg-red-50 border-red-200'
          : 'bg-green-50 border-green-200'
      ]"
    >
      <p :class="[
        'text-lg font-semibold',
        availableHours < 0 ? 'text-red-800' : 'text-green-800'
      ]">
        Horas disponibles:
        <span :class="availableHours < 0 ? 'text-red-600' : 'text-green-600'">
          {{ availableHours.toFixed(2) }}
        </span> horas
      </p>
    </div>

    <!-- Horas Consumidas -->
    <div v-if="selectedJanitor" class="mb-6">
      <!-- Header clickeable -->
      <div class="mb-4">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div
            @click="showSpentHours = !showSpentHours"
            class="flex items-center justify-between cursor-pointer p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex-1"
          >
            <h2 class="text-xl font-semibold text-gray-800">
              Horas Consumidas
              <span v-if="!loadingSpentHours" class="text-lg font-normal text-gray-600">
                ({{ totalSpentHours.toFixed(2) }} horas)
              </span>
            </h2>
            <svg
              :class="['w-5 h-5 text-gray-600 transition-transform', showSpentHours ? 'transform rotate-180' : '']"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <button
            @click="openUseHourModal"
            class="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors whitespace-nowrap"
          >
            Usar Horas
          </button>
        </div>
      </div>

      <!-- Loading state -->
      <div v-if="loadingSpentHours" class="text-center py-4">
        <p class="text-gray-600">Cargando horas consumidas...</p>
      </div>

      <!-- Collapsable content -->
      <div v-show="showSpentHours">
        <!-- Table -->
        <div v-if="spentHours.length > 0" class="py-2 -my-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
          <div class="inline-block min-w-full overflow-hidden align-middle border-b border-gray-200 shadow sm:rounded-lg">
            <table class="min-w-full">
              <thead>
                <tr>
                  <th class="px-6 py-3 text-xs font-solid leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                    Fecha
                  </th>
                  <th class="px-6 py-3 text-xs font-solid leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                    Hora Inicio - Hora Fin
                  </th>
                  <th class="px-6 py-3 text-xs font-solid leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                    Total (horas)
                  </th>
                  <th class="px-6 py-3 text-xs font-solid leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                    Creado por
                  </th>
                  <th class="px-6 py-3 text-xs font-solid leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                    Notas
                  </th>
                  <th class="px-6 py-3 text-xs font-solid leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white">
                <tr v-for="spentHour in spentHours" :key="spentHour.id" class="hover:bg-gray-50">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ formatDate(spentHour.consumed_date) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ formatTime(spentHour.consumed_start) }} - {{ formatTime(spentHour.consumed_end) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ formatHoursMinutes(spentHour.consumed_minutes) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ spentHour.created_by_name || '-' }}
                  </td>
                  <td class="px-6 py-4 text-sm text-gray-900">
                    {{ spentHour.notes || '-' }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <button
                      @click="openDeleteSpentHourModal(spentHour.id)"
                      class="text-red-600 hover:text-red-800 focus:outline-none"
                      title="Eliminar"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else class="text-center py-4">
          <p class="text-gray-600">No se encontraron horas consumidas para este personal.</p>
        </div>
      </div>
    </div>

    <!-- Compensatorios -->
    <div v-if="selectedJanitor" class="mb-6">
      <!-- Header clickeable -->
      <div class="mb-4">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div
            @click="showCompensatories = !showCompensatories"
            class="flex items-center justify-between cursor-pointer p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex-1"
          >
            <h2 class="text-xl font-semibold text-gray-800">
              Horas trabajadas
              <span v-if="!loadingCompensatories" class="text-lg font-normal text-gray-600">
                ({{ totalHours.toFixed(2) }} horas)
              </span>
            </h2>
            <svg
              :class="['w-5 h-5 text-gray-600 transition-transform', showCompensatories ? 'transform rotate-180' : '']"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <button
            @click="openAddHourModal"
            class="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors whitespace-nowrap"
          >
            Agregar Hora
          </button>
        </div>
      </div>

      <!-- Loading state -->
      <div v-if="loadingCompensatories" class="text-center py-8">
        <p class="text-gray-600">Cargando compensatorios...</p>
      </div>

      <!-- Collapsable content -->
      <div v-show="showCompensatories">
        <!-- Table -->
        <div v-if="compensatories.length > 0" class="py-2 -my-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
          <div class="inline-block min-w-full overflow-hidden align-middle border-b border-gray-200 shadow sm:rounded-lg">
            <table class="min-w-full">
              <thead>
                <tr>
                  <th class="px-6 py-3 text-xs font-solid leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                    Fecha
                  </th>
                  <th class="px-6 py-3 text-xs font-solid leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                    Hora Inicio - Hora Fin
                  </th>
                  <th class="px-6 py-3 text-xs font-solid leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                    Total (horas)
                  </th>
                  <!-- <th class="px-6 py-3 text-xs font-solid leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                    Estado
                  </th> -->
                  <th class="px-6 py-3 text-xs font-solid leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                    Notas
                  </th>
                  <th class="px-6 py-3 text-xs font-solid leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white">
                <tr v-for="compensatory in compensatories" :key="compensatory.id" class="hover:bg-gray-50">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ formatDate(compensatory.worked_start) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ formatTime(compensatory.worked_start) }} - {{ formatTime(compensatory.worked_end) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ formatHoursMinutes(compensatory.total_minutes) }}
                  </td>
                  <!-- <td class="px-6 py-4 whitespace-nowrap text-sm">
                    <span :class="getStatusClass(compensatory.status)" class="px-2 py-1 rounded-full text-xs font-medium">
                      {{ getStatusLabel(compensatory.status) }}
                    </span>
                  </td> -->
                  <td class="px-6 py-4 text-sm text-gray-900">
                    {{ compensatory.notes || '-' }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <button
                      @click="openDeleteEarnedHourModal(compensatory.id)"
                      class="text-red-600 hover:text-red-800 focus:outline-none"
                      title="Eliminar"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else class="text-center py-8">
          <p class="text-gray-600">No se encontraron compensatorios para este personal.</p>
        </div>
      </div>
    </div>

    <!-- Modal Agregar Hora -->
    <div v-if="showAddHourModal" class="fixed inset-0 flex items-center justify-center bg-gray-600 bg-opacity-50 z-50">
      <div class="bg-white rounded-lg overflow-hidden shadow-xl max-w-2xl w-full mx-4">
        <!-- Modal Header -->
        <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 class="text-xl font-semibold text-gray-800">Agregar Hora Trabajada</h2>
          <button
            @click="closeAddHourModal"
            class="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="p-6">
          <form @submit.prevent="submitEarnedHour">
            <!-- Personal Seleccionado -->
            <div class="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Personal
              </label>
              <p class="text-lg font-semibold text-gray-800">
                {{ selectedJanitorName }}
              </p>
            </div>

            <!-- Fecha -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2" for="worked_date">
                Fecha *
              </label>
              <input
                id="worked_date"
                v-model="earnedHourForm.worked_date"
                type="date"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <!-- Hora Inicio -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2" for="worked_start">
                Hora Inicio *
              </label>
              <div class="grid grid-cols-2 gap-3">
                <select
                  id="worked_start_hour"
                  v-model="earnedHourForm.worked_start_hour"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Hora</option>
                  <option v-for="hour in 24" :key="hour - 1" :value="String(hour - 1).padStart(2, '0')">
                    {{ String(hour - 1).padStart(2, '0') }}
                  </option>
                </select>
                <select
                  id="worked_start_minute"
                  v-model="earnedHourForm.worked_start_minute"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Min</option>
                  <option value="00">00</option>
                  <option value="30">30</option>
                </select>
              </div>
            </div>

            <!-- Hora Fin -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2" for="worked_end">
                Hora Fin *
              </label>
              <div class="grid grid-cols-2 gap-3">
                <select
                  id="worked_end_hour"
                  v-model="earnedHourForm.worked_end_hour"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Hora</option>
                  <option v-for="hour in 24" :key="hour - 1" :value="String(hour - 1).padStart(2, '0')">
                    {{ String(hour - 1).padStart(2, '0') }}
                  </option>
                </select>
                <select
                  id="worked_end_minute"
                  v-model="earnedHourForm.worked_end_minute"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Min</option>
                  <option value="00">00</option>
                  <option value="30">30</option>
                </select>
              </div>
            </div>

            <!-- Notas -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2" for="notes">
                Notas
              </label>
              <textarea
                id="notes"
                v-model="earnedHourForm.notes"
                rows="4"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Ej: Horas ganadas por trabajo extra en mantenimiento"
              ></textarea>
            </div>

            <!-- Error Message -->
            <div v-if="earnedHourError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p class="text-sm text-red-600">{{ earnedHourError }}</p>
            </div>

            <!-- Success Message -->
            <div v-if="earnedHourSuccess" class="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p class="text-sm text-green-600">{{ earnedHourSuccess }}</p>
            </div>

            <!-- Modal Footer -->
            <div class="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                @click="closeAddHourModal"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Cancelar
              </button>
              <button
                type="submit"
                :disabled="submittingEarnedHour"
                class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="submittingEarnedHour">Guardando...</span>
                <span v-else>Guardar</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal Usar Horas -->
    <div v-if="showUseHourModal" class="fixed inset-0 flex items-center justify-center bg-gray-600 bg-opacity-50 z-50">
      <div class="bg-white rounded-lg overflow-hidden shadow-xl max-w-2xl w-full mx-4">
        <!-- Modal Header -->
        <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 class="text-xl font-semibold text-gray-800">Usar Horas</h2>
          <button
            @click="closeUseHourModal"
            class="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="p-6">
          <form @submit.prevent="submitSpentHour">
            <!-- Personal Seleccionado -->
            <div class="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Personal
              </label>
              <p class="text-lg font-semibold text-gray-800">
                {{ selectedJanitorName }}
              </p>
            </div>

            <!-- Fecha de Consumo -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2" for="consumed_date">
                Fecha de Consumo *
              </label>
              <input
                id="consumed_date"
                v-model="spentHourForm.consumed_date"
                type="date"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <!-- Hora Inicio -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2" for="consumed_start">
                Hora Inicio *
              </label>
              <div class="grid grid-cols-2 gap-3">
                <select
                  id="consumed_start_hour"
                  v-model="spentHourForm.consumed_start_hour"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">Hora</option>
                  <option v-for="hour in 24" :key="hour - 1" :value="String(hour - 1).padStart(2, '0')">
                    {{ String(hour - 1).padStart(2, '0') }}
                  </option>
                </select>
                <select
                  id="consumed_start_minute"
                  v-model="spentHourForm.consumed_start_minute"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">Min</option>
                  <option value="00">00</option>
                  <option value="30">30</option>
                </select>
              </div>
            </div>

            <!-- Hora Fin -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2" for="consumed_end">
                Hora Fin *
              </label>
              <div class="grid grid-cols-2 gap-3">
                <select
                  id="consumed_end_hour"
                  v-model="spentHourForm.consumed_end_hour"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">Hora</option>
                  <option v-for="hour in 24" :key="hour - 1" :value="String(hour - 1).padStart(2, '0')">
                    {{ String(hour - 1).padStart(2, '0') }}
                  </option>
                </select>
                <select
                  id="consumed_end_minute"
                  v-model="spentHourForm.consumed_end_minute"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">Min</option>
                  <option value="00">00</option>
                  <option value="30">30</option>
                </select>
              </div>
            </div>

            <!-- Notas -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2" for="spent_notes">
                Notas
              </label>
              <textarea
                id="spent_notes"
                v-model="spentHourForm.notes"
                rows="4"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Ej: Horas gastadas en mantenimiento del edificio"
              ></textarea>
            </div>

            <!-- Error Message -->
            <div v-if="spentHourError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p class="text-sm text-red-600">{{ spentHourError }}</p>
            </div>

            <!-- Success Message -->
            <div v-if="spentHourSuccess" class="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p class="text-sm text-green-600">{{ spentHourSuccess }}</p>
            </div>

            <!-- Modal Footer -->
            <div class="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                @click="closeUseHourModal"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Cancelar
              </button>
              <button
                type="submit"
                :disabled="submittingSpentHour"
                class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="submittingSpentHour">Guardando...</span>
                <span v-else>Guardar</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal Confirmación Eliminar Hora Trabajada -->
    <ConfirmAction
      :isVisible="showDeleteEarnedHourModal"
      :processing="deletingEarnedHour"
      title="Eliminar Hora Trabajada"
      action="Eliminar"
      description="¿Está seguro que desea eliminar esta hora trabajada? Esta acción no se puede deshacer."
      @confirm="confirmDeleteEarnedHour"
      @cancel="closeDeleteEarnedHourModal"
    />

    <!-- Modal Confirmación Eliminar Hora Consumida -->
    <ConfirmAction
      :isVisible="showDeleteSpentHourModal"
      :processing="deletingSpentHour"
      title="Eliminar Hora Consumida"
      action="Eliminar"
      description="¿Está seguro que desea eliminar esta hora consumida? Esta acción no se puede deshacer."
      @confirm="confirmDeleteSpentHour"
      @cancel="closeDeleteSpentHourModal"
    />
  </div>
</template>

<script>
import ConfirmAction from '@/components/commons/ConfirmAction.vue';

export default {
  name: "CompensatoriosView",
  components: {
    ConfirmAction
  },
  data() {
    return {
      janitors: [],
      selectedJanitor: '',
      loading: false,
      compensatories: [],
      loadingCompensatories: false,
      spentHours: [],
      loadingSpentHours: false,
      showSpentHours: false,
      showCompensatories: false,
      showAddHourModal: false,
      submittingEarnedHour: false,
      earnedHourError: '',
      earnedHourSuccess: '',
      earnedHourForm: {
        worked_date: '',
        worked_start_hour: '',
        worked_start_minute: '00',
        worked_end_hour: '',
        worked_end_minute: '00',
        notes: ''
      },
      showUseHourModal: false,
      submittingSpentHour: false,
      spentHourError: '',
      spentHourSuccess: '',
      spentHourForm: {
        consumed_date: '',
        consumed_start_hour: '',
        consumed_start_minute: '00',
        consumed_end_hour: '',
        consumed_end_minute: '00',
        notes: ''
      },
      showDeleteEarnedHourModal: false,
      deletingEarnedHour: false,
      earnedHourToDelete: null,
      showDeleteSpentHourModal: false,
      deletingSpentHour: false,
      spentHourToDelete: null
    };
  },
  computed: {
    totalHours() {
      if (!this.compensatories || this.compensatories.length === 0) {
        return 0;
      }
      const totalMinutes = this.compensatories.reduce((sum, comp) => {
        return sum + (comp.total_minutes || 0);
      }, 0);
      return totalMinutes / 60;
    },
    totalSpentHours() {
      if (!this.spentHours || this.spentHours.length === 0) {
        return 0;
      }
      const totalMinutes = this.spentHours.reduce((sum, spentHour) => {
        return sum + (spentHour.consumed_minutes || 0);
      }, 0);
      return totalMinutes / 60;
    },
    availableHours() {
      return this.totalHours - this.totalSpentHours;
    },
    selectedJanitorName() {
      if (!this.selectedJanitor || !this.janitors.length) {
        return '';
      }
      const janitor = this.janitors.find(j => {
        const janitorId = j.User ? j.User.id : j.id;
        return janitorId === this.selectedJanitor;
      });
      if (janitor) {
        return janitor.Profile
          ? `${janitor.Profile.lastname}, ${janitor.Profile.firstname}`
          : janitor.name || janitor.email || '';
      }
      return '';
    }
  },
  watch: {
    selectedJanitor(newValue) {
      if (newValue) {
        this.getCompensatories(newValue);
        this.getSpentHours(newValue);
      } else {
        this.compensatories = [];
        this.spentHours = [];
      }
    }
  },
  mounted() {
    this.getJanitors();
  },
  methods: {
    getJanitors() {
      this.loading = true;
      this.$http.get('/roles')
        .then(res => {
          const role_janitor = res.data.find(r => r.name === 'janitor');
          if (role_janitor && role_janitor.id) {
            return this.$http.get('/user-by-role-id/' + role_janitor.id);
          } else {
            console.log('Rol janitor no encontrado');
            this.loading = false;
            return Promise.reject('Rol janitor no encontrado');
          }
        })
        .then(response => {
            console.log(" response.data", response.data);
          this.janitors = response.data.data || [];
          this.loading = false;
        })
        .catch(err => {
          console.error('Error obteniendo usuarios janitor:', err);
          this.janitors = [];
          this.loading = false;
        });
    },
    getCompensatories(janitorUserId) {
      this.loadingCompensatories = true;
      this.$http.get(`/users/${janitorUserId}/earned-hours`)
        .then(response => {
          console.log("Compensatorios response:", response.data);
          this.compensatories = response.data.earned_hours || [];
          this.loadingCompensatories = false;
        })
        .catch(err => {
          console.error('Error obteniendo compensatorios:', err);
          this.compensatories = [];
          this.loadingCompensatories = false;
        });
    },
    getSpentHours(janitorUserId) {
      this.loadingSpentHours = true;
      this.$http.get(`/users/${janitorUserId}/spent-hours`)
        .then(response => {
          console.log("Horas consumidas response:", response.data);
          this.spentHours = response.data.spent_hours || [];
          this.loadingSpentHours = false;
        })
        .catch(err => {
          console.error('Error obteniendo horas consumidas:', err);
          this.spentHours = [];
          this.loadingSpentHours = false;
        });
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      // Extraer fecha directamente del string ISO para evitar conversión de zona horaria
      // Maneja formatos como: "2026-01-10T00:00:00Z", "2026-01-10T00:00:00.000Z", "2026-01-10"
      const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        // Formatear como DD/MM/YYYY
        return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
      }
      // Fallback: usar UTC explícitamente
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        // Usar UTC para evitar conversión de zona horaria
        const day = String(date.getUTCDate()).padStart(2, '0');
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const year = date.getUTCFullYear();
        return `${day}/${month}/${year}`;
      } catch (e) {
        return '-';
      }
    },
    formatTime(timeString) {
      if (!timeString) return '-';
      // Extraer hora y minutos directamente del string ISO para evitar conversión de zona horaria
      // Maneja formatos como: "2026-01-09T21:00:00Z", "2026-01-09T21:00:00.000Z", "2026-01-09 21:00:00"
      const isoMatch = timeString.match(/T(\d{2}):(\d{2})/);
      if (isoMatch) {
        return `${isoMatch[1]}:${isoMatch[2]}`;
      }
      // Si no tiene formato ISO, intentar con espacio
      const spaceMatch = timeString.match(/\s(\d{2}):(\d{2})/);
      if (spaceMatch) {
        return `${spaceMatch[1]}:${spaceMatch[2]}`;
      }
      // Fallback: usar UTC explícitamente
      try {
        const date = new Date(timeString);
        if (isNaN(date.getTime())) return '-';
        // Usar UTC para evitar conversión de zona horaria
        const hours = String(date.getUTCHours()).padStart(2, '0');
        const minutes = String(date.getUTCMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
      } catch (e) {
        return '-';
      }
    },
    formatHoursMinutes(minutes) {
      if (!minutes && minutes !== 0) return '-';
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}:${String(mins).padStart(2, '0')}`;
    },
    getStatusLabel(status) {
      const statusMap = {
        'pending': 'Pendiente',
        'approved': 'Aprobado',
        'rejected': 'Rechazado',
        'completed': 'Completado'
      };
      return statusMap[status] || status;
    },
    getStatusClass(status) {
      const classMap = {
        'pending': 'bg-yellow-100 text-yellow-800',
        'approved': 'bg-green-100 text-green-800',
        'rejected': 'bg-red-100 text-red-800',
        'completed': 'bg-blue-100 text-blue-800'
      };
      return classMap[status] || 'bg-gray-100 text-gray-800';
    },
    openAddHourModal() {
      if (!this.selectedJanitor) {
        alert('Por favor seleccione un personal primero');
        return;
      }
      this.earnedHourForm = {
        worked_date: '',
        worked_start_hour: '',
        worked_start_minute: '00',
        worked_end_hour: '',
        worked_end_minute: '00',
        notes: ''
      };
      this.earnedHourError = '';
      this.earnedHourSuccess = '';
      this.showAddHourModal = true;
    },
    closeAddHourModal() {
      this.showAddHourModal = false;
      this.earnedHourForm = {
        worked_date: '',
        worked_start_hour: '',
        worked_start_minute: '00',
        worked_end_hour: '',
        worked_end_minute: '00',
        notes: ''
      };
      this.earnedHourError = '';
      this.earnedHourSuccess = '';
    },
    submitEarnedHour() {
      if (!this.selectedJanitor) {
        this.earnedHourError = 'Por favor seleccione un personal primero';
        return;
      }

      // Validar que todos los campos estén completos
      if (!this.earnedHourForm.worked_date || !this.earnedHourForm.worked_start_hour || !this.earnedHourForm.worked_start_minute ||
          !this.earnedHourForm.worked_end_hour || !this.earnedHourForm.worked_end_minute) {
        this.earnedHourError = 'Por favor complete todos los campos de fecha y hora';
        return;
      }

      // Combinar fecha de trabajo con hora y minutos
      const workedStartTime = `${this.earnedHourForm.worked_start_hour}:${this.earnedHourForm.worked_start_minute}:00`;
      const workedEndTime = `${this.earnedHourForm.worked_end_hour}:${this.earnedHourForm.worked_end_minute}:00`;
      const workedStartString = `${this.earnedHourForm.worked_date}T${workedStartTime}`;
      const workedEndString = `${this.earnedHourForm.worked_date}T${workedEndTime}`;

      // Validar que la fecha fin sea posterior a la fecha inicio usando Date.UTC para evitar conversión de zona horaria
      const [year, month, day] = this.earnedHourForm.worked_date.split('-').map(Number);
      const startDate = new Date(Date.UTC(year, month - 1, day,
        parseInt(this.earnedHourForm.worked_start_hour),
        parseInt(this.earnedHourForm.worked_start_minute), 0));
      const endDate = new Date(Date.UTC(year, month - 1, day,
        parseInt(this.earnedHourForm.worked_end_hour),
        parseInt(this.earnedHourForm.worked_end_minute), 0));

      if (endDate <= startDate) {
        this.earnedHourError = 'La hora de fin debe ser posterior a la hora de inicio';
        return;
      }

      this.submittingEarnedHour = true;
      this.earnedHourError = '';
      this.earnedHourSuccess = '';

      // Convertir a formato ISO 8601 sin conversión de zona horaria
      // Construir el string ISO directamente agregando Z para indicar UTC
      const workedStart = `${workedStartString}Z`;
      const workedEnd = `${workedEndString}Z`;

      const payload = {
        to_user_id: this.selectedJanitor,
        worked_start: workedStart,
        worked_end: workedEnd,
        notes: this.earnedHourForm.notes || ''
      };

      this.$http.post('/earned-hours', payload)
        .then(response => {
          console.log('Hora agregada exitosamente:', response.data);
          this.earnedHourSuccess = 'Hora trabajada agregada exitosamente';

          // Recargar los compensatorios
          this.getCompensatories(this.selectedJanitor);

          // Cerrar el modal después de un breve delay
          setTimeout(() => {
            this.closeAddHourModal();
          }, 1500);
        })
        .catch(err => {
          console.error('Error agregando hora trabajada:', err);
          this.earnedHourError = err.response?.data?.message || 'Error al agregar la hora trabajada. Por favor intente nuevamente.';
        })
        .finally(() => {
          this.submittingEarnedHour = false;
        });
    },
    openUseHourModal() {
      if (!this.selectedJanitor) {
        alert('Por favor seleccione un personal primero');
        return;
      }
      this.spentHourForm = {
        consumed_date: '',
        consumed_start_hour: '',
        consumed_start_minute: '00',
        consumed_end_hour: '',
        consumed_end_minute: '00',
        notes: ''
      };
      this.spentHourError = '';
      this.spentHourSuccess = '';
      this.showUseHourModal = true;
    },
    closeUseHourModal() {
      this.showUseHourModal = false;
      this.spentHourForm = {
        consumed_date: '',
        consumed_start_hour: '',
        consumed_start_minute: '00',
        consumed_end_hour: '',
        consumed_end_minute: '00',
        notes: ''
      };
      this.spentHourError = '';
      this.spentHourSuccess = '';
    },
    submitSpentHour() {
      if (!this.selectedJanitor) {
        this.spentHourError = 'Por favor seleccione un personal primero';
        return;
      }

      // Validar que todos los campos estén completos
      if (!this.spentHourForm.consumed_date ||
          !this.spentHourForm.consumed_start_hour || !this.spentHourForm.consumed_start_minute ||
          !this.spentHourForm.consumed_end_hour || !this.spentHourForm.consumed_end_minute) {
        this.spentHourError = 'Por favor complete todos los campos de fecha y hora';
        return;
      }

      // Combinar fecha de consumo con hora y minutos
      const consumedStartTime = `${this.spentHourForm.consumed_start_hour}:${this.spentHourForm.consumed_start_minute}:00`;
      const consumedEndTime = `${this.spentHourForm.consumed_end_hour}:${this.spentHourForm.consumed_end_minute}:00`;
      const consumedStartString = `${this.spentHourForm.consumed_date}T${consumedStartTime}`;
      const consumedEndString = `${this.spentHourForm.consumed_date}T${consumedEndTime}`;

      // Validar que la fecha fin sea posterior a la fecha inicio usando Date.UTC para evitar conversión de zona horaria
      const [year, month, day] = this.spentHourForm.consumed_date.split('-').map(Number);
      const startDate = new Date(Date.UTC(year, month - 1, day,
        parseInt(this.spentHourForm.consumed_start_hour),
        parseInt(this.spentHourForm.consumed_start_minute), 0));
      const endDate = new Date(Date.UTC(year, month - 1, day,
        parseInt(this.spentHourForm.consumed_end_hour),
        parseInt(this.spentHourForm.consumed_end_minute), 0));

      if (endDate <= startDate) {
        this.spentHourError = 'La fecha y hora de fin debe ser posterior a la fecha y hora de inicio';
        return;
      }

      this.submittingSpentHour = true;
      this.spentHourError = '';
      this.spentHourSuccess = '';

      // Convertir a formato ISO 8601 sin conversión de zona horaria
      // Construir el string ISO directamente agregando Z para indicar UTC
      const consumedDate = `${this.spentHourForm.consumed_date}T00:00:00Z`;
      const consumedStart = `${consumedStartString}Z`;
      const consumedEnd = `${consumedEndString}Z`;

      const payload = {
        to_user_id: this.selectedJanitor,
        consumed_date: consumedDate,
        consumed_start: consumedStart,
        consumed_end: consumedEnd,
        notes: this.spentHourForm.notes || ''
      };

      this.$http.post('/spent-hours', payload)
        .then(response => {
          console.log('Hora usada exitosamente:', response.data);
          this.spentHourSuccess = 'Horas usadas registradas exitosamente';

          // Recargar las horas consumidas
          this.getSpentHours(this.selectedJanitor);

          // Cerrar el modal después de un breve delay
          setTimeout(() => {
            this.closeUseHourModal();
          }, 1500);
        })
        .catch(err => {
          console.error('Error registrando horas usadas:', err);
          this.spentHourError = err.response?.data?.message || 'Error al registrar las horas usadas. Por favor intente nuevamente.';
        })
        .finally(() => {
          this.submittingSpentHour = false;
        });
    },
    openDeleteEarnedHourModal(id) {
      this.earnedHourToDelete = id;
      this.showDeleteEarnedHourModal = true;
    },
    closeDeleteEarnedHourModal() {
      this.showDeleteEarnedHourModal = false;
      this.earnedHourToDelete = null;
    },
    confirmDeleteEarnedHour() {
      if (!this.earnedHourToDelete) return;

      this.deletingEarnedHour = true;
      this.$http.delete(`/earned-hours/${this.earnedHourToDelete}`)
        .then(response => {
          console.log('Hora trabajada eliminada exitosamente:', response.data);
          // Recargar los compensatorios
          this.getCompensatories(this.selectedJanitor);
          this.closeDeleteEarnedHourModal();
        })
        .catch(err => {
          console.error('Error eliminando hora trabajada:', err);
          alert(err.response?.data?.message || 'Error al eliminar la hora trabajada. Por favor intente nuevamente.');
        })
        .finally(() => {
          this.deletingEarnedHour = false;
        });
    },
    openDeleteSpentHourModal(id) {
      this.spentHourToDelete = id;
      this.showDeleteSpentHourModal = true;
    },
    closeDeleteSpentHourModal() {
      this.showDeleteSpentHourModal = false;
      this.spentHourToDelete = null;
    },
    confirmDeleteSpentHour() {
      if (!this.spentHourToDelete) return;

      this.deletingSpentHour = true;
      this.$http.delete(`/spent-hours/${this.spentHourToDelete}`)
        .then(response => {
          console.log('Hora consumida eliminada exitosamente:', response.data);
          // Recargar las horas consumidas
          this.getSpentHours(this.selectedJanitor);
          this.closeDeleteSpentHourModal();
        })
        .catch(err => {
          console.error('Error eliminando hora consumida:', err);
          alert(err.response?.data?.message || 'Error al eliminar la hora consumida. Por favor intente nuevamente.');
        })
        .finally(() => {
          this.deletingSpentHour = false;
        });
    }
  }
};
</script>

<style scoped>
</style>

